import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  fullWidth?: boolean;
}

const TooltipContainer = styled.div<{ $fullWidth?: boolean }>`
  position: relative;
  display: ${({ $fullWidth }) => ($fullWidth ? 'block' : 'inline-block')};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

const TooltipContent = styled.div<{ position: string; visible: boolean }>`
  position: fixed;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 9999;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);

  ${({ position }) => {
    // Artık fixed positioning kullandığımız için bu CSS'lere ihtiyaç yok
    // Pozisyonlama JavaScript ile yapılıyor
    return '';
  }}
`;

const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  position = 'right',
  fullWidth = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateTooltipPosition = useCallback(() => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    
    // position: fixed kullanıldığı için viewport'a göre pozisyon
    // scrollTop eklenmemeli!
    let top = rect.top + (rect.height / 2) - 15;
    let left = 67; // Sidebar genişliği (65px) + 2px boşluk
    
    setTooltipPosition({ top, left });
  }, []);

  const handleMouseEnter = (e: React.MouseEvent) => {
    setIsVisible(true);
    updateTooltipPosition();
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Scroll sırasında tooltip pozisyonunu güncelle
  useEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => {
      updateTooltipPosition();
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isVisible, updateTooltipPosition]);

  return (
    <TooltipContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={containerRef}
      $fullWidth={fullWidth}
    >
      {children}
      {isVisible && (
        <TooltipContent 
          position={position} 
          visible={isVisible}
          ref={tooltipRef}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
        >
          {text}
        </TooltipContent>
      )}
    </TooltipContainer>
  );
};

export default Tooltip; 